import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DataComponentFilterOffer from './DataComponent__FilterOffer';
import DataComponentDisplayControl from './DataComponent__DisplayControl';
import DateComponentSettings from './DateComponent_Settings';
import DateComponentCurrencySettings from './DateComponent_CurrencySettings';
import Pagination from '../Pagination.js';
import query from '../../helpers/query';
import DataComponentBodyOffer from "./DataComponent__BodyOffer";

const DataComponentOffer = ({
    isQuery,
    loading,
    page,
    alignPagination,
    settings,
    fields,
    className,
    settingKey,
    columnData,
    displayControl,
    hideResetButton,
    gray,
    headerNames,
    tableBodyTitle,
    currencySettings,
    currencySettingKey,
    currencySettingData,
    data,
    multiLevelHeaderNames,
    totalPages,
    onAction
}) => {
    const [state, setState] = useState({
        filters: {},
        typeDisplayControl: 'table',
        page: page
    });

    const onActionHandler = useCallback((type) => {
        const actionState = {
            ...state.filters
        };

        onAction(actionState, type);

        if (isQuery) {
            query.replace(actionState);
        }
    }, [state.filters, onAction, isQuery]);

    const onPageChange = useCallback((page) => {
        setState(prev => ({
            ...prev,
            filters: {
                ...prev.filters,
                page
            }
        }));
        onActionHandler('pageChange');
    }, [onActionHandler]);

    const onFiltersApply = useCallback((state, type = 'filtersChange') => {
        setState(prev => ({
            ...prev,
            filters: {
                ...state
            }
        }));
        onActionHandler(type);
    }, [onActionHandler]);

    const onDisplayControlChange = useCallback((type) => {
        setState(prev => ({
            ...prev,
            typeDisplayControl: type
        }));
    }, []);

    const getClassFooter = useCallback(() => {
        return classNames({
            'ibox-footer': true,
            'text-right': alignPagination
        });
    }, [alignPagination]);

    useEffect(() => {
        setState(prev => ({
            ...prev,
            page: page || prev.page
        }));
    }, [page]);

    useEffect(() => {
        if (!fields || !fields.length) {
            onActionHandler('firstRender');
        }
    }, [fields?.length, onActionHandler]);

    return (
        <div className="ibox">
            {fields && fields.length
                ? (
                    <DataComponentFilterOffer
                        fields={fields}
                        loading={loading}
                        onAction={onFiltersApply}
                        hideResetButton={hideResetButton}
                        gray={gray}
                    />
                )
                : null
            }
            <DateComponentSettings 
                settings={settings}
                headerNames={headerNames}
                tableBodyTitle={tableBodyTitle}
                settingKey={settingKey}
                loading={loading}
                data={data}
                filters={state.filters}
            />
            <DateComponentCurrencySettings
                settings={currencySettings}
                headerNames={headerNames}
                tableBodyTitle={tableBodyTitle}
                settingKey={currencySettingKey}
                loading={loading}
                data={currencySettingData}
                filters={state.filters}
            />
            <div className="ibox-content">
                {displayControl
                    ? <DataComponentDisplayControl
                        data={displayControl.data}
                        type={state.typeDisplayControl}
                        name={displayControl.name}
                        onChange={onDisplayControlChange}
                      />
                    : null
                }
                <DataComponentBodyOffer
                    data={data}
                    headerNames={headerNames}
                    multiLevelHeaderNames={multiLevelHeaderNames}
                    columnData={columnData}
                    loading={loading}
                    settings={settings}
                    settingKey={settingKey}
                    displayControl={{
                        ...displayControl,
                        type: state.typeDisplayControl
                    }}
                    filters={state.filters}
                    className={className}
                />
            </div>
            {totalPages > 1
                ? <div className={getClassFooter()}>
                    <Pagination
                        onPageChange={onPageChange}
                        initialPage={state.filters.page}
                        totalPages={totalPages}
                        loading={loading}
                        align={alignPagination ? 'right' : 'left'}
                    />
                  </div>
                : null
            }
        </div>
    );
};

DataComponentOffer.propTypes = {
    isQuery: PropTypes.bool,
    loading: PropTypes.bool,
    page: PropTypes.number,
    alignPagination: PropTypes.bool,
    settings: PropTypes.bool,
    fields: PropTypes.array,
    className: PropTypes.string,
    settingKey: PropTypes.string,
    columnData: PropTypes.shape({
        header: PropTypes.object,
        body: PropTypes.object
    }),
    displayControl: PropTypes.shape({
        type: PropTypes.string,
        name: PropTypes.string,
        data: PropTypes.array
    }),
    hideResetButton: PropTypes.bool,
    gray: PropTypes.bool,
    headerNames: PropTypes.array,
    tableBodyTitle: PropTypes.string,
    currencySettings: PropTypes.bool,
    currencySettingKey: PropTypes.string,
    currencySettingData: PropTypes.array,
    data: PropTypes.array,
    multiLevelHeaderNames: PropTypes.array,
    totalPages: PropTypes.number,
    onAction: PropTypes.func
};

DataComponentOffer.defaultProps = {
    isQuery: false,
    loading: false,
    page: 1,
    alignPagination: false,
    settings: false,
    fields: [],
    className: '',
    settingKey: '',
    columnData: {
        header: {},
        body: {}
    },
    displayControl: null,
    hideResetButton: false,
    gray: false,
    headerNames: [],
    tableBodyTitle: '',
    currencySettings: false,
    currencySettingKey: '',
    currencySettingData: [],
    data: [],
    multiLevelHeaderNames: [],
    totalPages: 0,
    onAction: () => {}
};

export default DataComponentOffer;
